import React from "react";
import { Controller } from "react-hook-form";
import { IconCloudUpload, IconUpload, IconX } from "@tabler/icons-react";
import toast from "react-hot-toast";
import { getAcceptedFormats, FileUploadType } from "../constants/fileFormats";
import { T, useLanguage } from "../context/LanguageContext";

interface FilesType {
  images: boolean;
  imagesOdoMeter: boolean;
  rcDocs: boolean;
}

type MultiFileUploaderProps = {
  name: string;
  control: any;
  maxFiles?: number;
  allowedTypes?: string[];
  fileType?: FileUploadType;
  setSelectedFiles?: (
    val: FilesType | ((prev: FilesType) => FilesType),
  ) => void;
  existingImages?: string[];
};

const MultiFileUploader: React.FC<MultiFileUploaderProps> = ({
  name,
  control,
  maxFiles = 4,
  allowedTypes,
  fileType = "image",
  setSelectedFiles,
  existingImages = [],
}) => {
  const { t } = useLanguage();
  const dynamicAccept = getAcceptedFormats(fileType, allowedTypes?.join(","));
  const allowedTypesList = dynamicAccept
    .split(",")
    .map((t) => t.trim().toLowerCase());
  const inputRef = React.useRef<HTMLInputElement>(null);
  const MAX_SIZE = 1 * 1024 * 1024; // 1MB limit

  const getFileExtension = (file: File) =>
    file.name.split(".").pop()?.toLowerCase();

  const updateSelectedFilesState = (files: (File | string)[]) => {
    if (setSelectedFiles) {
      setSelectedFiles((prev: FilesType) => ({
        ...prev,
        [name]: files.length > 0,
      }));
    }
  };

  const getImageSrc = (file: File | string) => {
    if (typeof file === "string") {
      return file;
    }
    return URL.createObjectURL(file);
  };

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={existingImages}
      rules={{
        validate: (files: (File | string)[] | undefined) => {
          if (!files || files.length === 0) return true;

          if (files.length > maxFiles)
            return t("You can upload a maximum of {maxFiles} files").replace(
              "{maxFiles}",
              maxFiles.toString(),
            );

          const newFiles = files.filter(
            (file) => file instanceof File,
          ) as File[];
          for (const file of newFiles) {
            if (file.size > MAX_SIZE) {
              return t('File "{fileName}" exceeds 1MB limit.').replace(
                "{fileName}",
                file.name,
              );
            }
            const fileTypeMatches = allowedTypesList.some((spec) => {
              if (spec.startsWith("."))
                return getFileExtension(file) === spec.slice(1);
              if (spec.endsWith("/*"))
                return file.type.startsWith(spec.replace("/*", ""));
              return file.type === spec;
            });
            if (!fileTypeMatches) {
              return t("Only {formats} files are allowed").replace(
                "{formats}",
                dynamicAccept,
              );
            }
          }
          return true;
        },
      }}
      render={({ field, fieldState }) => {
        const currentFiles = field.value || [];
        const canAddMore = currentFiles.length < maxFiles;

        return (
          <div className="w-full">
            <input
              ref={inputRef}
              type="file"
              id={`upload-${name}`}
              className="hidden"
              multiple
              accept={dynamicAccept}
              onChange={(e) => {
                const selectedFiles = e.target.files
                  ? Array.from(e.target.files)
                  : [];
                if (selectedFiles.length === 0) return;

                const validFiles = selectedFiles.filter((file) => {
                  if (file.size > MAX_SIZE) {
                    return false;
                  }
                  return true;
                });

                if (validFiles.length < selectedFiles.length) {
                  toast.error(
                    t(
                      "File size limit exceeded. Please ensure each image is under 1MB.",
                    ),
                  );
                }

                const availableSlots = maxFiles - currentFiles.length;
                const finalFilesToAdd = validFiles.slice(0, availableSlots);

                if (finalFilesToAdd.length > 0) {
                  const updatedFiles = [...currentFiles, ...finalFilesToAdd];
                  field.onChange(updatedFiles);
                  updateSelectedFilesState(updatedFiles);
                }

                if (inputRef.current) inputRef.current.value = "";
              }}
            />

            {canAddMore && (
              <label
                htmlFor={`upload-${name}`}
                className="flex items-center justify-start py-1 px-2 border border-[#D2D2D2] rounded-xl cursor-pointer bg-white w-full"
              >
                <div className="flex items-center justify-center py-[4px] px-4 border border-[#D2D2D2] rounded-xl cursor-pointer bg-[#FBD037]/20">
                  <IconUpload stroke={1} size={20} />
                </div>
                <div className="text-sm text-gray-600 ms-2">
                  {t(
                    "Upload up to {maxFiles} images (PNG/JPG, Max 1MB each).",
                  ).replace("{maxFiles}", maxFiles.toString())}
                </div>
              </label>
            )}

            {fieldState.error && (
              <p className="text-red-500 text-sm mt-1">
                {t(fieldState.error.message || "")}
              </p>
            )}

            {currentFiles.length > 0 && (
              <div className="grid grid-cols-4 gap-4">
                {currentFiles.map((file: File | string, index: number) => {
                  const src = getImageSrc(file);
                  return (
                    <div
                      key={
                        typeof file === "string"
                          ? file
                          : `${file.name}-${index}`
                      }
                      className="relative"
                    >
                      <img
                        src={src}
                        alt="vehicle"
                        className="w-full h-32 object-cover rounded-md border"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const newFiles = currentFiles.filter(
                            (_: any, i: number) => i !== index,
                          );
                          field.onChange(newFiles);
                          updateSelectedFilesState(newFiles);
                          if (typeof file !== "string") {
                            URL.revokeObjectURL(src);
                          }
                        }}
                        className="absolute top-1 right-1 bg-white rounded-full p-1 shadow-sm hover:bg-gray-100"
                      >
                        <IconX
                          stroke={1.5}
                          size={14}
                          className="text-red-500"
                        />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      }}
    />
  );
};

export default MultiFileUploader;
